import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { useI18n } from "./I18nContext";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Phone, PhoneOff, PhoneCall } from "lucide-react";
import { toast } from "sonner";

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

function useRingtone(active) {
  const ctxRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let ctx, timer;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const beep = () => {
        if (stopped) return;
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = 480;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
        o.connect(g); g.connect(ctx.destination);
        o.start(); o.stop(ctx.currentTime + 0.65);
      };
      beep();
      timer = setInterval(beep, 2000);
    } catch (e) { console.debug("ringtone init failed", e); }
    return () => { stopped = true; if (timer) clearInterval(timer); if (ctx) ctx.close().catch(() => {}); };
  }, [active]);
}

export function CallProvider({ children }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [incoming, setIncoming] = useState(null); // {call_id, room_id, caller}
  const [outgoing, setOutgoing] = useState(null); // {call_id, room_id, callee}
  const incomingRef = useRef(null);
  const outgoingRef = useRef(null);
  incomingRef.current = incoming;
  outgoingRef.current = outgoing;

  const loggedIn = user && user !== false;
  const inRoom = pathname.startsWith("/room/");

  // poll for incoming calls
  useEffect(() => {
    if (!loggedIn || inRoom) return;
    const poll = async () => {
      if (outgoingRef.current) return;
      try {
        const { data } = await api.get("/call/incoming");
        if (data.incoming) {
          if (!incomingRef.current || incomingRef.current.call_id !== data.incoming.call_id) {
            setIncoming(data.incoming);
          }
        } else if (incomingRef.current) {
          setIncoming(null);
        }
      } catch (e) { console.debug("incoming-call poll failed", e); }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [loggedIn, inRoom]);

  const callFriend = useCallback(async (u) => {
    try {
      const { data } = await api.post("/call/invite", { callee_id: u.id });
      setOutgoing({ call_id: data.call_id, room_id: data.room_id, callee: u });
    } catch (e) {
      toast.error("Could not start the call.");
    }
  }, []);

  // poll outgoing call status
  useEffect(() => {
    if (!outgoing) return;
    let elapsed = 0;
    const id = setInterval(async () => {
      elapsed += 2;
      try {
        const { data } = await api.get(`/call/status/${outgoing.call_id}`);
        if (data.status === "accepted") {
          clearInterval(id);
          const callee = outgoing.callee;
          setOutgoing(null);
          nav(`/room/${data.room_id}`, { state: { partner: callee } });
        } else if (data.status === "declined") {
          clearInterval(id);
          toast.info(`${outgoing.callee.name} ${t("call.declined")}`);
          setOutgoing(null);
        }
      } catch (e) { console.debug("outgoing-call status poll failed", e); }
      if (elapsed >= 45) {
        clearInterval(id);
        api.post(`/call/hangup/${outgoing.call_id}`).catch(() => {});
        toast.info(t("call.noAnswer"));
        setOutgoing(null);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [outgoing, nav, t]);

  const cancelOutgoing = async () => {
    if (outgoing) await api.post(`/call/hangup/${outgoing.call_id}`).catch(() => {});
    setOutgoing(null);
  };
  const answerIncoming = async () => {
    if (!incoming) return;
    const caller = incoming.caller;
    const room = incoming.room_id;
    try { await api.post(`/call/accept/${incoming.call_id}`); } catch (e) { console.error("call accept failed", e); }
    setIncoming(null);
    nav(`/room/${room}`, { state: { partner: caller } });
  };
  const declineIncoming = async () => {
    if (incoming) await api.post(`/call/decline/${incoming.call_id}`).catch(() => {});
    setIncoming(null);
  };

  useRingtone(!!incoming);

  const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

  return (
    <CallContext.Provider value={{ callFriend, outgoing, incoming }}>
      {children}

      {/* Incoming call */}
      <Dialog open={!!incoming} onOpenChange={(o) => { if (!o) declineIncoming(); }}>
        <DialogContent className="rounded-3xl max-w-sm text-center" data-testid="incoming-call-dialog">
          <DialogTitle className="sr-only">{t("call.incomingTitle")}</DialogTitle>
          <div className="flex flex-col items-center py-6">
            <div className="relative mb-5">
              <span className="absolute inset-0 rounded-full bg-secondary/30 animate-ping" />
              <span className="relative w-20 h-20 rounded-3xl bg-accent flex items-center justify-center font-heading font-black text-2xl text-primary">
                {initials(incoming?.caller?.name)}
              </span>
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{t("call.incomingTitle")}</p>
            <h3 className="mt-1 font-heading font-black text-2xl">{incoming?.caller?.name}</h3>
            <p className="text-foreground/70 mt-1 text-sm">{t("call.isCalling")}</p>
            <div className="flex items-center gap-4 mt-7">
              <button onClick={declineIncoming} data-testid="decline-call-btn"
                className="w-14 h-14 rounded-full bg-destructive text-white flex items-center justify-center hover:brightness-110 transition">
                <PhoneOff className="w-6 h-6" />
              </button>
              <button onClick={answerIncoming} data-testid="answer-call-btn"
                className="w-14 h-14 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center hover:brightness-110 transition animate-bounce">
                <Phone className="w-6 h-6" />
              </button>
            </div>
            <div className="flex items-center gap-10 mt-2 text-xs text-muted-foreground">
              <span>{t("call.decline")}</span>
              <span>{t("call.answer")}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Outgoing call */}
      <Dialog open={!!outgoing} onOpenChange={(o) => { if (!o) cancelOutgoing(); }}>
        <DialogContent className="rounded-3xl max-w-sm text-center" data-testid="outgoing-call-dialog">
          <DialogTitle className="sr-only">{t("call.callingTitle")}</DialogTitle>
          <div className="flex flex-col items-center py-6">
            <div className="relative mb-5">
              <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
              <span className="relative w-20 h-20 rounded-3xl bg-accent flex items-center justify-center font-heading font-black text-2xl text-primary">
                {initials(outgoing?.callee?.name)}
              </span>
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{t("call.callingTitle")}</p>
            <h3 className="mt-1 font-heading font-black text-2xl">{outgoing?.callee?.name}</h3>
            <p className="text-foreground/70 mt-1 text-sm flex items-center gap-1"><PhoneCall className="w-4 h-4" /> {t("call.ringing")}</p>
            <Button variant="ghost" className="rounded-full mt-6 text-destructive hover:bg-destructive/10" onClick={cancelOutgoing} data-testid="cancel-call-btn">
              <PhoneOff className="w-4 h-4 mr-1" /> {t("call.cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </CallContext.Provider>
  );
}
